import { attachmentKind, useAttachmentLinks, useIsOnline, useSubmissionUpload } from '@shkola/api';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_FILES = 6;
const MAX_SIDE = 2000;

/** A phone photo is often 5–10 MB: re-encoded to at most 2000 px it is a few hundred KB. */
async function shrinkImage(file: File): Promise<{ blob: Blob; extension: string }> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    if (blob) return { blob, extension: 'jpg' };
  } catch {
    // a format the browser cannot draw (HEIC on Chrome): sent as it is
  }
  return { blob: file, extension: file.name.split('.').pop()?.toLowerCase() || 'jpg' };
}

/** The recorder's own format: webm in Chrome and Firefox, mp4 in Safari. */
const recordingType = () =>
  ['audio/webm', 'audio/mp4'].find((type) => MediaRecorder.isTypeSupported(type)) ?? '';

/** Photos, recordings and PDFs of an answer, shown small; a tap opens them. */
export function AttachmentList({
  paths,
  onRemove,
  dark = false,
}: {
  paths: ReadonlyArray<string>;
  onRemove?: (path: string) => void;
  dark?: boolean;
}) {
  const { t } = useTranslation();
  const links = useAttachmentLinks(paths);
  if (paths.length === 0) return null;
  return (
    <ul className="flex flex-wrap items-start gap-2">
      {paths.map((path) => {
        const url = links.data?.[path];
        const kind = attachmentKind(path);
        return (
          <li key={path} className="relative">
            {kind === 'image' ? (
              <a href={url} target="_blank" rel="noreferrer" className="block">
                {url ? (
                  <img src={url} alt="" className="size-24 rounded-2xl object-cover" />
                ) : (
                  <span className="block size-24 animate-pulse rounded-2xl bg-fill" />
                )}
              </a>
            ) : kind === 'audio' ? (
              <span
                className={`flex items-center gap-2 rounded-2xl px-3 py-2 ${dark ? 'bg-white/10' : 'bg-fill'}`}
              >
                🎙{url && <audio controls src={url} className="h-9 max-w-56" />}
              </span>
            ) : (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={`flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold ${dark ? 'bg-white/10' : 'bg-fill text-ink'}`}
              >
                📄 PDF
              </a>
            )}
            {onRemove && (
              <button
                type="button"
                aria-label={t('homework.files.remove')}
                onClick={() => onRemove(path)}
                className="absolute -top-2 -right-2 flex size-6 items-center justify-center rounded-full bg-ink text-xs text-white"
              >
                ×
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * «Здати як зручно»: the answer may be a photo of the notebook, a voice recording or a PDF as well
 * as text. Files go up at once (they need a connection); the answer itself can still wait.
 */
export function AnswerFiles({
  schoolId,
  homeworkId,
  studentId,
  value,
  onChange,
}: {
  schoolId: string;
  homeworkId: string;
  studentId: string;
  value: string[];
  onChange: (paths: string[]) => void;
}) {
  const { t } = useTranslation();
  const online = useIsOnline();
  const upload = useSubmissionUpload();
  const photos = useRef<HTMLInputElement>(null);
  const pdf = useRef<HTMLInputElement>(null);
  const [recording, setRecording] = useState<{ recorder: MediaRecorder; started: number }>();
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState<string>();
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    if (!recording) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [recording]);

  const add = async (blob: Blob, extension: string) => {
    if (latest.current.length >= MAX_FILES) return;
    setError(undefined);
    try {
      const path = await upload.mutateAsync({
        schoolId,
        homeworkId,
        studentId,
        file: blob,
        extension,
      });
      onChange([...latest.current, path]);
    } catch {
      setError(t('homework.files.failed'));
    }
  };

  const record = async () => {
    if (recording) {
      recording.recorder.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = recordingType();
      const recorder = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        setRecording(undefined);
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        void add(blob, blob.type.includes('mp4') ? 'm4a' : 'webm');
      };
      recorder.start();
      setRecording({ recorder, started: Date.now() });
      setNow(Date.now());
    } catch {
      setError(t('homework.files.noMicrophone'));
    }
  };

  const seconds = recording ? Math.floor((now - recording.started) / 1000) : 0;
  const full = value.length >= MAX_FILES;
  return (
    <div className="space-y-3">
      <AttachmentList
        paths={value}
        onRemove={(path) => onChange(value.filter((p) => p !== path))}
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!online || full || upload.isPending}
          onClick={() => photos.current?.click()}
          className="rounded-full bg-fill px-4 py-2 text-sm font-semibold text-ink disabled:opacity-40"
        >
          📷 {t('homework.files.photo')}
        </button>
        <button
          type="button"
          disabled={!online || (full && !recording) || upload.isPending}
          onClick={() => void record()}
          className={`rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40 ${recording ? 'bg-red-pen text-white' : 'bg-fill text-ink'}`}
        >
          {recording
            ? `■ ${t('homework.files.stop')} · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
            : `🎙 ${t('homework.files.voice')}`}
        </button>
        <button
          type="button"
          disabled={!online || full || upload.isPending}
          onClick={() => pdf.current?.click()}
          className="rounded-full bg-fill px-4 py-2 text-sm font-semibold text-ink disabled:opacity-40"
        >
          📄 PDF
        </button>
        {upload.isPending && (
          <span className="self-center text-sm text-muted">{t('homework.files.uploading')}</span>
        )}
      </div>
      {!online && <p className="text-sm text-muted">{t('homework.files.offline')}</p>}
      {error && <p className="text-sm text-red-pen">{error}</p>}
      <input
        ref={photos}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])].slice(0, MAX_FILES - value.length);
          e.target.value = '';
          void (async () => {
            for (const file of files) {
              const { blob, extension } = await shrinkImage(file);
              await add(blob, extension);
            }
          })();
        }}
      />
      <input
        ref={pdf}
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void add(file, 'pdf');
        }}
      />
    </div>
  );
}
